import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import * as timeago from 'timeago.js'
import map from 'lodash/map'
import getProjectStatusForProjectsList from '@Utils/GetProjectStatusForProjectsList'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getShouldAllowToGenerateClips from '@Utils/GetShouldAllowToGenerateClips'
import getFormattedDuration from '@Utils/GetFormattedDuration'
import getCleanDisplayTitle from '@Utils/GetCleanDisplayTitle'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import TitleTooltip from '@SiteComponents/TitleTooltip'
import ProjectActionDropdown from './ProjectActionDropdown'
import Done from '@Images/projects_table_list_done.svg'
import InProgress from '@Images/projects_table_list_in_progress.svg'
import More from '@Images/projects_table_list_more.svg'

import {
  IProject,
} from '@SiteModules/Project/types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IOpenRenameProjectPopupPayload,
  IOpenDeleteProjectPopupPayload,
  IOpenSetupProjectPopupPayload,
  IOpenSetupProjectPopupTtsPayload,
} from '@SiteModules/Popup/types'

import './ProjectRow.scss'

const MILLISECONDS_IN_SECONDS = 1000
const TITLE_EMOJI = '🎬'
const TITLE_EMOJI_TTS = '🗣'
const TITLE_EMOJI_TRANSLATIONS = '🌐'

interface ProjectRowProps {
  readonly index: number
  readonly project: IProject
  readonly originalProjectVersion: IProjectVersion
  readonly selected: boolean
  readonly currentAccountHasCurrentSubscription: boolean
  changeSelectedProjects(e: React.ChangeEvent<HTMLInputElement>, projectId: string): void
  handleForwarding(project: IProject): void
  handleOpenRenameProjectPopup(payload: IOpenRenameProjectPopupPayload): void
  handleOpenDeleteProjectPopup(payload: IOpenDeleteProjectPopupPayload): void
  handleOpenSetupProjectPopup(payload: IOpenSetupProjectPopupPayload): void
  handleOpenSetupProjectPopupTts(payload: IOpenSetupProjectPopupTtsPayload): void
  handleOpenRenewSubscriptionPopup(): void
  handleOpenExportationPopup(projectId: string): void
}

const ProjectRow: React.FC<ProjectRowProps> = props => {
  const {
    project,
    index,
    selected,
    originalProjectVersion,
    currentAccountHasCurrentSubscription,
  } = props

  const {
    id,
    projectVersions,
  } = project

  const {
    title,
  } = project.attributes

  const {
    subtitles,
  } = originalProjectVersion

  const {
    status,
    duration,
    thumbnailUrl,
    mostRecentUpdatedAtTimestamp,
    createdAt,
    belongsToTtsProject,
    isPreparingRepurposedClips,
  } = originalProjectVersion.attributes

  const {
    showTooltip,
    setShowTooltip,
    triggerShowTooltip,
    cancelTooltip,
  } = useTooltipDelay()

  const imgRef = useRef<HTMLImageElement | null>(null)
  const checkboxRef = useRef<HTMLTableCellElement | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [fitClass, setFitClass] = useState('cover')

  const isDownloading = status === 'downloading'
  const shouldShowLinkToEditor = subtitles.length === 0 && !isDownloading && !isPreparingRepurposedClips
  const shouldOpenLanguagesView = projectVersions.length === 1 && (subtitles.length > 0 || isPreparingRepurposedClips)
  const shouldOpenProjectVersionsView = projectVersions.length > 1 && subtitles.length > 0
  const shouldShowLink = shouldShowLinkToEditor || shouldOpenLanguagesView || shouldOpenProjectVersionsView
  const shouldAllowToAddLanguages = getShouldAllowToAddLanguages(originalProjectVersion.attributes)
  const shouldAllowToGenerateClips = getShouldAllowToGenerateClips(originalProjectVersion.attributes, originalProjectVersion.subtitles)
  const moreElementId = `project_row_more_${id}`
  const classNameForClickOutsideListenerMoreDropdown = `ProjectsTable__action-dropdown-element-${id}`
  const numberOfClips = projectVersions.length - 1
  const hasClips = numberOfClips > 0
  const hasTranslationsOfOriginalAndNoClips = originalProjectVersion.subtitles.length > 1 && !hasClips
  const cleanTitle = getCleanDisplayTitle(title)

  useEffect(() => {
    const img = imgRef.current
    if (img && img.complete) {
      applyFitClass(img)
    } else if (img) {
      img.onload = () => applyFitClass(img)
    }
  }, [])

  const applyFitClass = (img: HTMLImageElement) => {
    const { naturalWidth, naturalHeight } = img
    if (naturalWidth >= naturalHeight) {
      setFitClass('cover')
    } else {
      setFitClass('contain')
    }
  }

  const onMouseEnter = () => {
    checkboxRef.current?.style?.setProperty('visibility', 'visible')
  }

  const onMouseLeave = () => {
    if (selected) { return }

    checkboxRef.current?.style?.setProperty('visibility', 'hidden')
  }

  const onMouseEnterTitle = () => {
    triggerShowTooltip()
  }

  const onMouseLeaveTitle = () => {
    cancelTooltip()
  }

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const handleOpenExportationPopup = () => {
    props.handleOpenExportationPopup(project.id)
    setShowDropdown(false)
  }

  const handleProjectSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.changeSelectedProjects(e, id)
  }

  const handleClickOnLink = () => {
    if (shouldShowLink) {
      props.handleForwarding(project)
    }
  }

  const handleOpenRenameProjectPopup = () => {
    props.handleOpenRenameProjectPopup({ projectId: id })
    setShowDropdown(false)
  }

  const handleOpenDeleteProjectPopup = () => {
    props.handleOpenDeleteProjectPopup({ projectIds: [id] })
    setShowDropdown(false)
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = (projectType?: string) => {
    if (currentAccountHasCurrentSubscription) {
      belongsToTtsProject ? props.handleOpenSetupProjectPopupTts({ projectVersionIds: [originalProjectVersion.id] }) : props.handleOpenSetupProjectPopup({ projectVersionIds: [originalProjectVersion.id], projectType })
    } else {
      props.handleOpenRenewSubscriptionPopup()
    }

    setShowDropdown(false)
  }

  const renderCheckbox = () => {
    return (
      <label className="ProjectsTable__checkbox ProjectRow__checkbox">
        <input
          type="checkbox"
          onChange={handleProjectSelection}
          checked={selected}
        />
        <div className="ProjectRow__checkmark" />
      </label>
    )
  }

  const renderThumbnail = () => {
    const shouldShowColoredThumbnail = thumbnailUrl.includes('thumbnail-icon.png') || !thumbnailUrl
    const indexOfFirstIntegerInId = id.search(/[0-9]/)
    const colorPicker = indexOfFirstIntegerInId === -1 ? 0 : id[indexOfFirstIntegerInId]

    return (
      <React.Fragment>
        {shouldShowColoredThumbnail ? (
          <div className={`ProjectRow__thumbnail-placeholder ProjectsTable__thumbnail-placeholder${colorPicker}`} />
        ) : (
          <div className="ProjectRow__thumbnail-image-wrapper with-blur-small">
            <img
              className="ProjectRow__thumbnail-image background-blur"
              src={thumbnailUrl}
              aria-hidden="true"
              loading="lazy"
              decoding="async"
            />
            <img
              className={`ProjectRow__thumbnail-image foreground ${fitClass}`}
              src={thumbnailUrl}
              alt="Project thumbnail"
              ref={imgRef}
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderTitle = () => {
    let titleEmoji = TITLE_EMOJI
    if (belongsToTtsProject) {
      titleEmoji = TITLE_EMOJI_TTS
    } else if (hasTranslationsOfOriginalAndNoClips) {
      titleEmoji = TITLE_EMOJI_TRANSLATIONS
    }

    return (
      <React.Fragment>
        <div
          className="ProjectRow__title"
          id={`project_row_title_${id}`}
          onMouseEnter={onMouseEnterTitle}
          onMouseLeave={onMouseLeaveTitle}
        >
          {titleEmoji} {cleanTitle}
        </div>
        {showTooltip && renderTooltip()}
      </React.Fragment>
    )
  }

  const renderTooltip = () => {
    return (
      <TitleTooltip
        id={id}
        title={cleanTitle}
        elementId={`project_row_title_${id}`}
        tooltipOffsetX={-80}
        tooltipOffsetY={8}
        classNameOfScrollContainer="ProjectsTableList"
        setShowTooltip={setShowTooltip}
      />
    )
  }

  const renderStatus = () => {
    const statuses = map(project.projectVersions, pv => pv.attributes.status)
    const [projectStatusClass, projectStatusToRender] = getProjectStatusForProjectsList(statuses)
    const isDone = projectStatusClass === 'done'
    const isInProgress = projectStatusClass === 'in_progress'

    return (
      <div className={`ProjectRow__status-badge ProjectsTable__status-badge ${projectStatusClass}`}>
        {projectStatusToRender}
        {isDone && <Done />}
        {isInProgress && <InProgress />}
      </div>
    )
  }

  const renderActionDropdown = () => {
    return (
      <ProjectActionDropdown
        showDropdown={showDropdown}
        projectId={id}
        canManageProjectSharing={project.attributes.canManageProjectSharing}
        classNameForClickOutsideListenerMoreDropdown={classNameForClickOutsideListenerMoreDropdown}
        moreElementId={moreElementId}
        setShowDropdown={setShowDropdown}
        shouldAllowToAddLanguages={shouldAllowToAddLanguages}
        shouldAllowToGenerateClips={shouldAllowToGenerateClips}
        openSetupProjectOrRenewSubscriptionPopup={handleOpenSetupProjectOrRenewSubscriptionPopup}
        openRenameProjectPopup={handleOpenRenameProjectPopup}
        openDeleteProjectPopup={handleOpenDeleteProjectPopup}
        handleOpenExportationPopup={handleOpenExportationPopup}
        listActive
      />
    )
  }

  return (
    <tr
      className={classnames(
        { 'ProjectRow__even': index % 2 === 0 },
        { 'ProjectRow__uneven': index % 2 === 1 }
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <td className="ProjectsTableList__check ProjectRow__check" ref={checkboxRef}>
        {renderCheckbox()}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__thumbnail-title',
          'ProjectRow__thumbnail-title',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        <div className="ProjectRow__thumbnail-title-wrapper">
          {renderThumbnail()}
          {renderTitle()}
        </div>
      </td>
      <td
        className={classnames(
          'ProjectsTableList__status',
          'ProjectRow__status',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {renderStatus()}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__clips',
          'ProjectRow__clips',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {numberOfClips !== 0 ? numberOfClips : isPreparingRepurposedClips ? (
          <span className="ProjectRow__clips-preparing">
            Preparing
          </span>
        ) : null}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__languages',
          'ProjectRow__languages',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {subtitles.length}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__duration',
          'ProjectRow__duration',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {getFormattedDuration(duration)}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__edited',
          'ProjectRow__edited',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {timeago.format(mostRecentUpdatedAtTimestamp * MILLISECONDS_IN_SECONDS)}
      </td>
      <td
        className={classnames(
          'ProjectsTableList__created',
          'ProjectRow__created',
          { 'ProjectRow__link': shouldShowLink }
        )}
        onClick={handleClickOnLink}
      >
        {createdAt}
      </td>
      <td className="ProjectsTableList__more ProjectRow__more">
        <div
          className={`ProjectRow__more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
          id={moreElementId}
        >
          <More
            onClick={toggleDropdown}
          />
          {showDropdown && renderActionDropdown()}
        </div>
      </td>
    </tr>
  )
}

export default ProjectRow
